/**
 * Conjunction-level clause splitting — the vocabulary-aware half of chaining
 * (plan §5).
 *
 * @remarks
 * After punctuation splitting, a clause may still chain commands with
 * conjunction words: `open the door then descend`, `take card and swipe it`.
 * The subtlety is `and`, which is equally at home INSIDE a noun phrase
 * (`the black and white keycard`). The rule, deterministic and cheap:
 *
 *   - A maximal run of conjunction tokens is a boundary when it contains
 *     `then` (an unambiguous chain marker), OR when the token immediately
 *     after the run begins a new clause — i.e. the vocabulary recognizes it
 *     as an intent or direction (`...and swipe...`, `...and north...`).
 *   - Otherwise the run is ordinary words and stays in place
 *     (`black and white keycard` remains one phrase).
 *   - Boundary runs are consumed; empty segments (leading/trailing/doubled
 *     markers) are dropped.
 *
 * The vocabulary arrives through the narrow {@link ConjunctionSplitVocabulary}
 * port rather than the full compiled lexicon, so this module is testable in
 * isolation and `CompiledLexicon` simply satisfies the interface later
 * (structural typing doing boundary work).
 *
 * @packageDocumentation
 */

import { type Token } from '../types.js';

/** The built-in chain-conjunction tokens. Callers cannot extend these in v1. */
export const CHAIN_CONJUNCTION_TOKENS = ['and', 'then'] as const;

/**
 * The narrow vocabulary port conjunction splitting needs: one question —
 * "does this token begin a new clause?" (`CompiledLexicon` answers yes for
 * intent tokens, their aliases, and direction tokens.)
 */
export interface ConjunctionSplitVocabulary {
	isClauseLeadingToken(tokenText: string): boolean;
}

/** A non-empty token segment — the emptiness contract made type-visible, so consumers never re-prove it. */
export type ClauseTokenSegment = readonly [Token, ...Token[]];

/**
 * Split one token stream into chained-command segments.
 *
 * @param tokens - A punctuation-free clause's tokens
 * @param vocabulary - The clause-leader oracle (see {@link ConjunctionSplitVocabulary})
 * @returns Non-empty token segments in input order (one segment when nothing chains)
 */
export const splitOnConjunctions = (
	tokens: readonly Token[],
	vocabulary: ConjunctionSplitVocabulary,
): readonly ClauseTokenSegment[] => {
	const segments: ClauseTokenSegment[] = [];

	// A single narrowed-token pass: conjunctions accumulate into a pending run
	// until the next ordinary token (or the stream's end) decides whether the
	// run was a boundary or noun-phrase material. Iterating the tokens directly
	// means no index ever needs an undefined-guard (coverage ruling, 2026-08-12).
	let currentSegment: Token[] = [];
	let didRunContainThen = false;
	let pendingConjunctionRun: Token[] = [];

	const finishSegment = (): void => {
		const [firstSegmentToken, ...restSegmentTokens] = currentSegment;

		if (firstSegmentToken !== undefined) {
			segments.push([firstSegmentToken, ...restSegmentTokens]);
			currentSegment = [];
		}
	};

	const discardRun = (): void => {
		didRunContainThen = false;
		pendingConjunctionRun = [];
	};

	const keepRunAsWords = (): void => {
		currentSegment.push(...pendingConjunctionRun);
		discardRun();
	};

	for (const currentToken of tokens) {
		if (isChainConjunction(currentToken.text)) {
			pendingConjunctionRun.push(currentToken);
			didRunContainThen ||= currentToken.text === 'then';
			continue;
		}

		if (pendingConjunctionRun.length > 0) {
			if (didRunContainThen || vocabulary.isClauseLeadingToken(currentToken.text)) {
				finishSegment();
				discardRun();
			} else {
				// Noun-phrase conjunction(s) — keep the run's tokens in place.
				keepRunAsWords();
			}
		}

		currentSegment.push(currentToken);
	}

	// A trailing run has no following token to judge it: `then` still chains
	// (and is dropped); a bare trailing `and` stays an ordinary word.
	if (pendingConjunctionRun.length > 0) {
		if (didRunContainThen) {
			finishSegment();
		} else {
			keepRunAsWords();
		}
	}

	finishSegment();

	return segments;
};

/**
 * Is this token one of the built-in chain conjunctions?
 *
 * @param tokenText - The candidate token text
 * @returns Whether it can participate in a boundary run
 */
const isChainConjunction = (tokenText: string): boolean =>
	(CHAIN_CONJUNCTION_TOKENS as readonly string[]).includes(tokenText);
